import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, signal, computed } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoggerAssociation } from './interfaces';
import { CrmTruncatePipe } from '../../../../shared-pipes/truncate.pipe';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'crm-activity-logger-association',
  templateUrl: './association.component.html',
  styleUrls: ['./association.component.scss'],
  imports: [
    CommonModule,
    MatIconModule,
    MatChipsModule,
    MatInputModule,
    ReactiveFormsModule,
    TextFieldModule,
    MatAutocompleteModule,
    MatTooltipModule,
    CrmTruncatePipe,
  ],
  standalone: true,
})
export class CrmActivityLoggerAssociationComponent {
  private _associations = signal<LoggerAssociation[]>([]);
  @Input({ required: true }) set associations(associationList: LoggerAssociation[]) {
    this._associations.set(associationList);
  }
  get associations(): LoggerAssociation[] {
    return this._associations();
  }
  editableAssociations = computed(() => {
    const associations = this._associations();
    const editableAssociations = associations.filter((association) => association?.name !== '');
    return editableAssociations;
  });
  @Input() fixedId: string | null = null;
  @Input({ transform: coerceBooleanProperty }) mobile: boolean | null = null;
  @Input({ required: true }) options: LoggerAssociation[] = [];
  @Input() placeholder = '';

  @Output() add = new EventEmitter<LoggerAssociation>();
  @Output() remove = new EventEmitter<LoggerAssociation>();
  @Output() searchTermChange = new EventEmitter<string | null>();

  protected showAdditionalChips = false;
  protected readonly separatorKeyCodes: number[] = [ENTER, COMMA];

  searchCtrl = new FormControl<string>('');

  constructor() {
    this.searchCtrl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((searchTerm) => this.searchTermChange.emit(searchTerm));
  }

  handleRemoveOption(option: LoggerAssociation): void {
    this.removeAssociation(option);
  }

  handleSelectOption(event: MatAutocompleteSelectedEvent): void {
    if (!event.option.value) {
      return;
    }
    this.addAssociation(event.option.value);
    this.searchCtrl.setValue('');
  }

  private removeAssociation(association: LoggerAssociation) {
    if (association.crmObjectId !== this.fixedId) {
      this.remove.emit(association);
    }
  }

  private addAssociation(association: LoggerAssociation): void {
    if (!association.crmObjectType || !association.crmObjectId) {
      return;
    }
    if (!this.associations.find((a) => a.crmObjectId === association.crmObjectId)) {
      this.add.emit(association);
    }
  }
}
